<template>
  <div class="witcher-layout">
    <!-- Header -->
    <WitcherHeader
      :announcement-text="announcementText"
      @action-button-click="handleActionButtonClick"
      @menu-click="handleMenuClick"
      @show-login-modal="handleShowLoginModal"
      @login-success="handleLoginSuccess"
    />

    <!-- Content Area -->
    <main class="witcher-content">
      <transition name="content-fade" mode="out-in">
        <router-view
          @show-login-modal="handleShowLoginModal"
          @login-success="handleLoginSuccess"
        />
      </transition>
    </main>

    <!-- Bottom Navigator -->
    <WitcherBottomNavigator
      :is-hidden="isNavigatorHidden"
      :nav-items="navItems"
      @nav-item-click="handleNavItemClick"
      @navigate-home="handleNavigateHome"
    />

    <!-- Sidebar Menu - Compact Design -->
    <b-sidebar
      id="witcher-sidebar"
      v-model="sidebarVisible"
      right
      backdrop
      shadow
      no-header
      sidebar-class="sidebar-compact"
    >
      <div class="sidebar-compact-wrapper">
        <!-- Close Button -->
        <button class="sc-close" @click="sidebarVisible = false">
          <feather-icon icon="XIcon" size="18" />
        </button>

        <!-- Balance Row (only when logged in) -->
        <div v-if="isLoggedIn" class="sc-balance">
          <span class="sc-balance-label">ยอดเงิน</span>
          <span class="sc-balance-amount"
            >฿{{ formatBalance(displayBalance) }}</span
          >
        </div>

        <!-- Quick Actions -->
        <div class="sc-actions">
          <button class="sc-btn primary" @click="navigateTo('/deposit')">
            <feather-icon icon="PlusCircleIcon" size="18" />
            <span>ฝาก</span>
          </button>
          <button class="sc-btn secondary" @click="navigateTo('/withdraw')">
            <feather-icon icon="MinusCircleIcon" size="18" />
            <span>ถอน</span>
          </button>
        </div>

        <!-- Navigation -->
        <nav class="sc-nav">
          <div
            class="sc-nav-item"
            :class="{
              active: $route.path === '/dashboard' || $route.path === '/'
            }"
            @click="navigateTo('/dashboard')"
          >
            <feather-icon icon="HomeIcon" size="18" />
            <span>หน้าหลัก</span>
          </div>
          <div
            class="sc-nav-item"
            :class="{ active: $route.path === '/personal-info' }"
            @click="navigateTo('/personal-info')"
          >
            <feather-icon icon="UserIcon" size="18" />
            <span>ข้อมูลส่วนตัว</span>
          </div>
          <div
            class="sc-nav-item"
            :class="{ active: $route.path === '/promotion' }"
            @click="navigateTo('/promotion')"
          >
            <feather-icon icon="GiftIcon" size="18" />
            <span>โปรโมชั่น</span>
          </div>
          <div
            class="sc-nav-item"
            :class="{ active: $route.path === '/cashback' }"
            @click="navigateTo('/cashback')"
          >
            <feather-icon icon="RefreshCwIcon" size="18" />
            <span>คืนยอด</span>
          </div>
        </nav>

        <!-- Contact -->
        <div class="sc-contact" @click="handleContactAdmin">
          <feather-icon icon="MessageCircleIcon" size="18" />
          <span>ติดต่อแอดมิน</span>
        </div>

        <!-- ฺBackup Contact -->
        <div class="sc-contact" @click="handleContactAdmin">
          <feather-icon icon="MessageCircleIcon" size="18" />
          <span>ไลน์สำรอง</span>
        </div>

        <!-- Logout -->
        <div v-if="isLoggedIn" class="sc-logout" @click="handleLogout">
          <feather-icon icon="LogOutIcon" size="18" />
          <span>ออกจากระบบ</span>
        </div>

        <!-- Footer -->
        <div class="sc-footer">
          © 2026 Kaiju168
        </div>
      </div>
    </b-sidebar>

    <!-- Login Modal for Auth Checks -->
    <login-modal
      :show="showLoginModal"
      @close="showLoginModal = false"
      @login="handleLoginSuccess"
    />

    <!-- Announcement Popup -->
    <announcement-popup
      ref="announcementPopup"
      v-model="showAnnouncementPopup"
      :announcements="activeAnnouncements"
      @close="handleAnnouncementClose"
      @dismiss="handleAnnouncementDismiss"
      @image-click="handleAnnouncementClick"
    />
  </div>
</template>

<script>
import { BSidebar } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import { isUserLoggedIn, logoutUser } from '@/auth/utils'
import { announcementService } from '@/services/announcementService'
import WitcherHeader from './components/WitcherHeader.vue'
import WitcherBottomNavigator from './components/WitcherBottomNavigator.vue'

// Lazy load auth modal to reduce initial bundle size
const LoginModal = () =>
  import(
    /* webpackChunkName: "auth-modals" */ '../../../views/v3/component/LoginModal.vue'
  )

// Lazy load announcement popup
const AnnouncementPopup = () =>
  import(
    /* webpackChunkName: "announcement-popup" */ '../../../views/v3/component/AnnouncementPopup.vue'
  )

// Ids of popups with frequency='once_per_session' already shown in this tab.
const SESSION_SEEN_KEY = 'elz:popup:session:v1'

export default {
  name: 'LayoutWitcher',
  components: {
    WitcherHeader,
    WitcherBottomNavigator,
    BSidebar,
    LoginModal,
    AnnouncementPopup
  },
  data() {
    return {
      isNavigatorHidden: false,
      lastScrollY: 0,
      sidebarVisible: false,
      showLoginModal: false,
      showAnnouncementPopup: false,
      activeAnnouncements: [],
      lastFetchedPage: null,
      announcementText:
        '🌟 เว็บรวมเกมส์อันดับ 1 ของไทย เหนือกว่า ในทุกด้าน มีทุกเกมส์ มีทุกค่าย ให้เลือกเล่นมากมาย ระบบฝาก-ถอน ออโต้เพียง 1 วิ',
      navItems: [
        { icon: '🏠', label: 'Home', route: '/dashboard' },
        { icon: '🎰', label: 'Casino', route: '/casino' },
        { icon: '⚽', label: 'Sports', route: '/sport' },
        { icon: '🎁', label: 'Promos', route: '/promotion' }
      ]
    }
  },
  computed: {
    ...mapGetters('member', ['contactUrl']),
    isLoggedIn() {
      return isUserLoggedIn()
    },
    displayBalance() {
      return this.$store.getters['member/displayBalance'] || 0
    }
  },
  watch: {
    $route: {
      handler(newRoute) {
        this.fetchAnnouncementsForPage(newRoute.name)
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      const appElement = document.querySelector('#app')
      if (appElement) {
        appElement.addEventListener('scroll', this.handleScroll, {
          passive: true
        })
      }
    })

    if (this.isLoggedIn) {
      this.$store.dispatch('member/fetchContactUrl')
    }

    // Check if login modal should be shown based on route query
    if (this.$route.query.showLogin === 'true') {
      this.showLoginModal = true
      // Clear the query parameter
      this.$router.replace({ query: {} })
    }

    // Fetch and show announcements after 500ms delay
    setTimeout(() => {
      this.fetchAnnouncementsForPage(this.$route.name)
    }, 500)
  },
  beforeDestroy() {
    const appElement = document.querySelector('#app')
    if (appElement) {
      appElement.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    handleScroll() {
      const appElement = document.querySelector('#app')
      const currentScrollY = appElement ? appElement.scrollTop : 0

      if (currentScrollY > this.lastScrollY && currentScrollY > 50) {
        this.isNavigatorHidden = true
      } else if (currentScrollY < this.lastScrollY) {
        this.isNavigatorHidden = false
      }

      this.lastScrollY = currentScrollY
    },
    handleActionButtonClick(action) {
      console.log('Action button clicked:', action)
      // Handle different action types
      if (action === 'game') {
        this.$router.push('/gameplay')
      } else if (action === 'money') {
        this.$router.push('/member/deposit')
      }
    },
    handleMenuClick() {
      this.sidebarVisible = !this.sidebarVisible
    },
    handleNavItemClick(item, index) {
      console.log('Nav item clicked:', item, index)
      if (item.route) {
        // Check if route requires authentication by checking if it's one of our protected routes
        const protectedRoutes = [
          '/dashboard',
          '/promotion',
          '/deposit',
          '/withdraw',
          '/cashback',
          '/referral'
        ]
        const requiresAuth = protectedRoutes.includes(item.route)

        if (requiresAuth && !isUserLoggedIn()) {
          console.log(
            `[LayoutWitcher] Route ${item.route} requires auth, showing login modal`
          )
          this.handleShowLoginModal()
          return
        }

        console.log(`[LayoutWitcher] Navigating to ${item.route}`)
        // Avoid redundant navigation if already at the target route
        if (this.$route.path !== item.route) {
          this.$router.push(item.route)
        }
      }
    },
    navigateTo(route) {
      // Check if route requires authentication
      const protectedRoutes = [
        '/dashboard',
        '/promotion',
        '/deposit',
        '/withdraw',
        '/cashback',
        '/referral'
      ]
      const requiresAuth = protectedRoutes.includes(route)

      if (requiresAuth && !isUserLoggedIn()) {
        console.log(
          `[LayoutWitcher] Sidebar route ${route} requires auth, showing login modal`
        )
        this.sidebarVisible = false
        this.handleShowLoginModal()
        return
      }

      // Avoid redundant navigation if already at the target route
      if (this.$route.path !== route) {
        this.$router.push(route)
      }
      this.sidebarVisible = false
    },
    handleNavigateHome() {
      // This method is called when user clicks center button while already on homepage
      // It should reset the homepage to its initial state
      console.log(
        '[LayoutWitcher] Navigate home event received - resetting homepage state'
      )

      // We can emit an event to the child components or use a more direct approach
      // For now, we'll use the $root event bus to communicate with HomePage
      this.$root.$emit('reset-homepage-state')
    },
    handleShowLoginModal() {
      this.showLoginModal = true
    },
    handleLoginSuccess() {
      this.showLoginModal = false

      console.log(
        '[LayoutWitcher] Login successful, authentication state updated'
      )

      // Fetch contact URL now that user is logged in
      this.$store.dispatch('member/fetchContactUrl')
    },
    async handleLogout() {
      try {
        console.log('[LayoutWitcher] Logging out user')

        // Close sidebar first
        this.sidebarVisible = false

        // Call logout utility function
        await logoutUser(this.$router)

        console.log('[LayoutWitcher] Logout successful')
      } catch (error) {
        console.error('[LayoutWitcher] Error during logout:', error)
        // Even if there's an error, we still want to clear local state
        this.sidebarVisible = false
      }
    },
    formatBalance(value) {
      return new Intl.NumberFormat('th-TH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0)
    },
    handleContactAdmin() {
      if (this.contactUrl) {
        window.open(this.contactUrl, '_blank')
      }
      this.sidebarVisible = false
    },

    // Announcement methods

    // frequency='once_per_session' is the one rule the backend cannot enforce
    // — it deliberately returns those rows every time and leaves the
    // suppression to us (see Agent-Amb-Service PopupAnnouncementService
    // .shouldShowPopup). sessionStorage clears itself when the tab closes,
    // which is exactly the lifetime we want.
    getSessionSeenIds() {
      try {
        const raw = sessionStorage.getItem(SESSION_SEEN_KEY)
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) ? parsed : []
      } catch (error) {
        return []
      }
    },

    markSessionSeen(announcementId) {
      if (announcementId == null) return
      try {
        const seen = this.getSessionSeenIds()
        if (!seen.includes(announcementId)) {
          seen.push(announcementId)
          sessionStorage.setItem(SESSION_SEEN_KEY, JSON.stringify(seen))
        }
      } catch (error) {
        // Private-mode / quota failures are not worth breaking the popup over
      }
    },

    async fetchAnnouncementsForPage(routeName) {
      // Only fetch announcements for logged-in users
      if (!isUserLoggedIn()) return

      const page = announcementService.getPageFromRoute(routeName)

      // Skip if already fetched for this page
      if (this.lastFetchedPage === page) return
      this.lastFetchedPage = page

      try {
        const announcements = await announcementService.fetchAnnouncements(
          this.$http,
          page
        )

        const seen = this.getSessionSeenIds()
        // Always reassign so a page with no popups clears the previous page's
        // list instead of leaving it behind the closed modal.
        this.activeAnnouncements = announcements.filter(
          a => !(a.frequency === 'once_per_session' && seen.includes(a.id))
        )

        if (this.activeAnnouncements.length > 0) {
          this.showAnnouncementPopup = true
        }
      } catch (error) {
        console.error('[LayoutWitcher] Failed to fetch announcements:', error)
      }
    },

    handleAnnouncementClose() {
      this.showAnnouncementPopup = false
      // Closing counts as "seen" for this session — dismissal is not required.
      this.activeAnnouncements
        .filter(a => a.frequency === 'once_per_session')
        .forEach(a => this.markSessionSeen(a.id))
    },

    async handleAnnouncementDismiss(announcementId) {
      this.markSessionSeen(announcementId)

      const success = await announcementService.dismissAnnouncement(
        this.$http,
        announcementId
      )

      // If API failed, revert the checkbox state
      if (!success && this.$refs.announcementPopup) {
        this.$refs.announcementPopup.revertDismiss(announcementId)
      }
    },

    handleAnnouncementClick(announcement) {
      console.log('[LayoutWitcher] Announcement clicked:', announcement.name)
    }
  }
}
</script>

<style lang="scss" scoped>
.witcher-layout {
  min-height: 100vh;
  background: transparent;
  position: relative;
}

.witcher-content {
  min-height: calc(100vh - 170px); // Adjust for header and navigator
  padding: 10px 8px 80px;
  position: relative;
}

// Content transitions
.content-fade-enter-active,
.content-fade-leave-active {
  transition: all 0.3s ease;
}

.content-fade-enter,
.content-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

// Compact Sidebar Styling
.sidebar-compact-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px;
  padding-top: 48px; // Space for close button
  gap: 8px;

  // Close button - top right corner
  .sc-close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;

    &:active {
      transform: scale(0.95);
      background: rgba(255, 255, 255, 0.12);
    }
  }

  // Balance (uses gold/secondary)
  .sc-balance {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: rgba(var(--theme-secondary-rgb), 0.1);
    border: 1px solid rgba(var(--theme-secondary-rgb), 0.2);
    border-radius: 10px;

    .sc-balance-label {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.6);
    }

    .sc-balance-amount {
      font-size: 16px;
      font-weight: 700;
      color: var(--theme-secondary);
    }
  }

  // Quick Actions
  .sc-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    .sc-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 12px 8px;
      border: none;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s ease;

      &.primary {
        background: var(--theme-gradient-primary);
        color: #fff;
      }

      &.secondary {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(var(--theme-secondary-rgb), 0.25);
        color: var(--theme-secondary);
      }

      &:active {
        transform: scale(0.97);
      }
    }
  }

  // Navigation
  .sc-nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  .sc-nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;

    &:active {
      background: rgba(255, 255, 255, 0.05);
    }

    &.active {
      background: rgba(var(--theme-secondary-rgb), 0.12);
      color: var(--theme-secondary);

      svg {
        color: var(--theme-secondary);
      }
    }
  }

  // Contact
  .sc-contact {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 12px;
    background: rgba(34, 197, 94, 0.08);
    border: 1px solid rgba(34, 197, 94, 0.2);
    border-radius: 10px;
    cursor: pointer;
    color: #22c55e;
    font-size: 14px;
    transition: all 0.15s ease;

    &:active {
      background: rgba(34, 197, 94, 0.15);
    }
  }

  // Logout
  .sc-logout {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 12px;
    border-radius: 8px;
    cursor: pointer;
    color: #ef4444;
    font-size: 14px;
    transition: all 0.15s ease;

    &:active {
      background: rgba(239, 68, 68, 0.1);
    }
  }

  // Footer
  .sc-footer {
    margin-top: auto;
    padding-top: 8px;
    text-align: center;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.25);
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }
}
</style>

<style lang="scss">
// Global compact sidebar styling
.b-sidebar.sidebar-compact {
  width: min(80vw, 260px) !important;
  max-width: 260px !important;
  background: var(--theme-sidebar-bg) !important;
  color: #fff !important;
  border-left: 1px solid rgba(var(--theme-secondary-rgb), 0.1) !important;

  .b-sidebar-body {
    background: transparent !important;
    padding: 0 !important;
    color: #fff !important;
  }

  .b-sidebar-header {
    display: none !important;
  }
}

.b-sidebar-backdrop {
  background-color: rgba(0, 0, 0, 0.6) !important;
}
</style>
